/**
 * OKRDashboard Component (Refactored)
 *
 * UNIFIED OKR SCREEN - Single view that adapts based on user role
 * This is the refactored version using extracted components and hooks.
 */

import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Layout } from '@layout/Layout';
import { OKRDashboardProvider, useOKRDashboardContext } from './OKRDashboardContext';
import { AlertNotifications } from './shared/AlertNotifications';
import { DashboardHeader } from './sections/DashboardHeader';
import { OrgObjectiveCard } from './sections/OrgObjectiveCard';
import { StrategicUnitsCard } from './sections/StrategicUnitsCard';
import { DepartmentsCard } from './sections/DepartmentsCard';
import { TeamMembersCard } from './sections/TeamMembersCard';
import { TeamObjectiveManagementCard } from './sections/TeamObjectiveManagementCard';
import { MilestonesExecutionCard } from './sections/MilestonesExecutionCard';
import { MyMilestonesCard } from './sections/MyMilestonesCard';
import { RetrospectiveNotesCard } from './sections/RetrospectiveNotesCard';
import { CreateOrgObjectiveDialog } from './dialogs/CreateOrgObjectiveDialog';
import { EditOrgObjectiveDialog } from './dialogs/EditOrgObjectiveDialog';
import { CreateTeamDialog } from './dialogs/CreateTeamDialog';
import { CreateTeamObjectiveDialog } from './dialogs/CreateTeamObjectiveDialog';
import { EditTeamObjectiveDialog } from './dialogs/EditTeamObjectiveDialog';
import { AddNoteDialog } from './dialogs/AddNoteDialog';
import { FeedbackDialog } from './dialogs/FeedbackDialog';
import { EditMilestoneDialog } from './dialogs/EditMilestoneDialog';
import { EditIndividualObjectiveDialog } from './dialogs/EditIndividualObjectiveDialog';
import { AddMilestoneDialog } from './dialogs/AddMilestoneDialog';
import { MemberViewDialog } from './dialogs/MemberViewDialog';
import { CreatePeriodDialog } from './dialogs/CreatePeriodDialog';
import { EditPeriodDialog } from './dialogs/EditPeriodDialog';
import { DeletePeriodConfirmDialog } from './dialogs/DeletePeriodConfirmDialog';
import AtomicMapPanel from '../../components/AtomicMapPanel';

/**
 * Inner content component that uses the context
 */
const OKRDashboardContent: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const notesRef = useRef<HTMLDivElement>(null);
    const highlightApplied = useRef(false);
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));
    const isLargeTablet = useMediaQuery(muiTheme.breakpoints.down('lg'));

    const {
        isDarkMode,
        data,
        permissions,
        tasks,
        sections,
    } = useOKRDashboardContext();

    const {
        loading,
        error,
        success,
        setError,
        setSuccess,
        atomicMapData,
        departments,
        employees,
        userContext,
    } = data;

    const { selectedNode, setSelectedNode } = tasks;
    const { setExpandedSections } = sections;

    // Reset highlightApplied when highlight param appears (allows re-scrolling on navigation)
    useEffect(() => {
        const highlight = searchParams.get('highlight');
        if (highlight === 'notes') {
            // Reset the flag to allow scrolling when navigating from notifications
            highlightApplied.current = false;
        }
    }, [searchParams]);

    // Handle deep linking for notes section
    useEffect(() => {
        const highlight = searchParams.get('highlight');
        if (highlight === 'notes' && !loading && !highlightApplied.current) {
            highlightApplied.current = true;

            // Expand notes section
            setExpandedSections(prev => ({ ...prev, notes: true }));

            // Scroll to notes section after a short delay to allow rendering
            setTimeout(() => {
                if (notesRef.current) {
                    notesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Add highlight animation
                    notesRef.current.classList.add('highlight-pulse');
                    setTimeout(() => {
                        notesRef.current?.classList.remove('highlight-pulse');
                    }, 2000);
                }
                // Clear the URL param
                searchParams.delete('highlight');
                setSearchParams(searchParams, { replace: true });
            }, 500);
        }
    }, [loading, searchParams, setExpandedSections, setSearchParams]);

    // Loading State
    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    gap: 2,
                }}
            >
                <CircularProgress size={48} sx={{ color: '#6366f1' }} />
                <Typography
                    sx={{
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontWeight: 500,
                    }}
                >
                    Loading OKR Dashboard...
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: isDarkMode ? '#0f172a' : '#f8fafc',
            }}
        >
            {/* Alert Notifications */}
            <AlertNotifications
                error={error}
                success={success}
                onCloseError={() => setError(null)}
                onCloseSuccess={() => setSuccess(null)}
            />

            {/* Sticky Header */}
            <DashboardHeader />

            {/* Main Content Area */}
            <Box sx={{ p: { xs: 1.5, sm: 2, md: 3, lg: 4 } }}>
                {/* Top Row: Org Objective + Strategic Units | OKR Map */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    gap: { xs: 2, sm: 3, lg: 4 },
                    alignItems: 'flex-start',
                    mb: { xs: 2, sm: 3, lg: 4 },
                }}>
                    {/* Left Column: Org + Strategic Units */}
                    <Box sx={{
                        flex: { lg: '1 1 60%' },
                        minWidth: 0,
                        width: { xs: '100%', lg: 'auto' },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: 2, sm: 3, lg: 4 },
                    }}>
                        <OrgObjectiveCard />
                        <StrategicUnitsCard />
                    </Box>

                    {/* Right Column: Atomic Map */}
                    <Box
                        sx={{
                            flex: { lg: '0 0 38%' },
                            width: { xs: '100%', lg: 'auto' },
                            position: { lg: 'sticky' },
                            top: { lg: 100 },
                            maxHeight: { lg: 'calc(100vh - 120px)' },
                        }}
                    >
                        <AtomicMapPanel
                            data={atomicMapData}
                            onNodeClick={setSelectedNode}
                            selectedNode={selectedNode}
                            userContext={userContext}
                            permissions={permissions}
                            departments={departments}
                            employees={employees}
                        />
                    </Box>
                </Box>

                {/* Full Width Sections Below */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3, lg: 4 } }}>
                    {/* Role-based Sections */}
                    {permissions.canViewAllTeams ? (
                        // Admin/HR View (+ Manager sections if also a manager)
                        <>
                            <DepartmentsCard />
                            {permissions.isManager && (
                                <>
                                    <TeamMembersCard />
                                    <TeamObjectiveManagementCard />
                                    <MilestonesExecutionCard />
                                </>
                            )}
                        </>
                    ) : permissions.isManager ? (
                        // Manager View
                        <>
                            <TeamMembersCard />
                            <TeamObjectiveManagementCard />
                            <MilestonesExecutionCard />
                        </>
                    ) : (
                        // Employee View
                        <>
                            <MyMilestonesCard />
                            <TeamObjectiveManagementCard />
                        </>
                    )}

                    {/* Retrospective Notes - visible to all users */}
                    {permissions.canViewNotes && (
                        <Box
                            ref={notesRef}
                            sx={{
                                '&.highlight-pulse': {
                                    animation: 'highlightPulse 0.5s ease-in-out 3',
                                },
                                '@keyframes highlightPulse': {
                                    '0%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.4)' },
                                    '50%': { boxShadow: '0 0 0 12px rgba(245, 158, 11, 0)' },
                                    '100%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0)' },
                                },
                            }}
                        >
                            <RetrospectiveNotesCard />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Dialogs */}
            <CreateOrgObjectiveDialog />
            <EditOrgObjectiveDialog />
            <CreateTeamDialog />
            <CreateTeamObjectiveDialog />
            <EditTeamObjectiveDialog />
            <AddNoteDialog />
            <FeedbackDialog />
            <EditMilestoneDialog />
            <EditIndividualObjectiveDialog />
            <AddMilestoneDialog />
            <MemberViewDialog />
            <CreatePeriodDialog />
            <EditPeriodDialog />
            <DeletePeriodConfirmDialog />
        </Box>
    );
};

/**
 * Main OKRDashboard component with provider wrapper
 */
const OKRDashboard: React.FC = () => {
    return (
        <OKRDashboardProvider>
            <Layout>
                <OKRDashboardContent />
            </Layout>
        </OKRDashboardProvider>
    );
};

export default OKRDashboard;
